import {
    Component,
    signal,
    OnInit,
    output,
    ElementRef,
    ViewChild,
    computed,
    OnDestroy,
    ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AudioDeviceService } from '@dxp/core/services';
import { TelXLDropdownComponent, TelXLButtonComponent } from '@dxp/elements';
import { delay, of, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { IDropdown } from '@dxp/core/models';

@Component({
    selector: 'voice-audio',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TelXLDropdownComponent, TelXLButtonComponent, CommonModule],
    templateUrl: 'voice-audio.component.html',
    styleUrl: 'voice-audio.component.scss',
})
export class VoiceAudioComponent implements OnInit, OnDestroy {
    constructor(public audioDeviceService: AudioDeviceService) {}

    saved = output();
    readonly microphones = signal<MediaDeviceInfo[]>([]);
    readonly speakers = signal<MediaDeviceInfo[]>([]);
    readonly micValue = signal<string>('');
    readonly speakerValue = signal<string>('');
    paused = signal(false);
    micTestRunning = computed(() => this.audioDeviceService.micTestRunning());

    @ViewChild('testAudio') testAudioRef!: ElementRef<HTMLAudioElement>;

    bars = Array.from({ length: 16 }, (_, i) => i);
    baseHeights = [
        50, 25, 5, 50, 75, 100, 50, 50, 25, 50, 50, 100, 75, 50, 5, 25, 50,
    ];

    audioProgress = signal(0);
    audioDuration = signal(0);
    playbackProgress = signal(0);
    isPlaying = signal(false);

    private updateProgressInterval: any = null;
    private playbackContext: AudioContext | null = null;
    private playbackAnalyser: AnalyserNode | null = null;
    private playbackDataArray: Uint8Array | null = null;
    private playbackRafId: number | null = null;
    private autoStopSub?: Subscription;
    private destroy$ = new Subject<void>();

    getScaledBarHeight(i: number): number {
        const base = this.baseHeights[i];
        const volume = this.audioDeviceService.micVolume() * 3;
        return Math.max(2, (base * volume) / 100);
    }

    toggleMicTest() {
        const running = this.audioDeviceService.micTestRunning();
        if (!running) {
            this.resetAudio();
            this.isPlaying.set(false);
            this.audioProgress.set(0);
            clearInterval(this.updateProgressInterval);
            this.stopPlaybackBars();
        }
        this.audioDeviceService.toggleMicTest();

        if (running) {
            of(null)
                .pipe(
                    delay(100),
                    tap(() => this.togglePlayback()),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    private resetAudio() {
        const audio = this.testAudioRef.nativeElement;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        this.audioProgress.set(0);
        this.isPlaying.set(false);
    }

    togglePlayback() {
        const audioEl = this.testAudioRef.nativeElement;
        if (!audioEl.src) {
            const url = this.audioDeviceService.getTestAudioUrl();
            if (!url) return;

            audioEl.src = url;
            this.audioDeviceService.applySpeakerTo(audioEl);
            audioEl.onloadedmetadata = () => {
                this.audioDuration.set(audioEl.duration || 0);
                this.startPlayback();
            };
            return;
        }

        if (audioEl.paused) {
            this.startPlayback();
        } else {
            this.pausePlayback(audioEl);
        }
    }

    startPlayback() {
        const oldAudio = this.testAudioRef?.nativeElement;
        if (!oldAudio) return;

        if (oldAudio.paused && oldAudio.currentTime > 0 && !oldAudio.ended) {
            oldAudio
                .play()
                .then(() => {
                    this.isPlaying.set(true);
                    this.animatePlaybackBars();
                    oldAudio.ontimeupdate = () => {
                        const progress =
                            (oldAudio.currentTime / oldAudio.duration) * 100;
                        this.audioProgress.set(progress);
                    };
                })
                .catch(console.error);
            return;
        }

        const newAudio = oldAudio.cloneNode(true) as HTMLAudioElement;
        newAudio.src = oldAudio.src;
        newAudio.currentTime = 0;

        oldAudio.parentNode?.replaceChild(newAudio, oldAudio);
        (this.testAudioRef as any).nativeElement = newAudio;

        this.setupPlaybackAnalyser();
        this.animatePlaybackBars();

        newAudio.onloadedmetadata = () => {
            if (newAudio.duration === Infinity) {
                newAudio.currentTime = 1e101;
                newAudio.ontimeupdate = () => {
                    newAudio.ontimeupdate = null;
                    newAudio.currentTime = 0;

                    this.audioDuration.set(newAudio.duration);

                    newAudio
                        .play()
                        .then(() => {
                            this.isPlaying.set(true);
                            this.updateProgressInterval = setInterval(() => {
                                const progress =
                                    (newAudio.currentTime / newAudio.duration) *
                                    100;
                                this.audioProgress.set(progress);
                            }, 100);
                        })
                        .catch(console.error);

                    newAudio.onended = () => {
                        this.isPlaying.set(false);
                        this.audioProgress.set(100);
                        clearInterval(this.updateProgressInterval);
                        this.stopPlaybackBars();
                    };
                };
            } else {
                this.audioDuration.set(newAudio.duration);

                newAudio
                    .play()
                    .then(() => {
                        this.isPlaying.set(true);
                        this.updateProgressInterval = setInterval(() => {
                            const progress =
                                (newAudio.currentTime / newAudio.duration) *
                                100;
                            this.audioProgress.set(progress);
                        }, 100);
                    })
                    .catch(console.error);

                newAudio.onended = () => {
                    this.isPlaying.set(false);
                    this.audioProgress.set(100);
                    clearInterval(this.updateProgressInterval);
                    this.stopPlaybackBars();
                };
            }
        };
    }

    animatePlaybackBars() {
        const loop = () => {
            if (!this.playbackAnalyser || !this.playbackDataArray) return;
            this.playbackAnalyser.getByteTimeDomainData(this.playbackDataArray);

            const avg =
                this.playbackDataArray.reduce(
                    (sum, val) => sum + Math.abs(val - 128),
                    0,
                ) / this.playbackDataArray.length;

            const amplified = Math.min(100, avg * 1.5);
            this.audioDeviceService.micVolume.set(amplified);

            this.playbackRafId = requestAnimationFrame(loop);
        };
        loop();
    }

    stopPlaybackBars() {
        if (this.playbackRafId) cancelAnimationFrame(this.playbackRafId);
        this.playbackContext?.close();

        this.playbackContext = null;
        this.playbackAnalyser = null;
        this.playbackDataArray = null;
        this.playbackRafId = null;

        this.audioDeviceService.micVolume.set(2);
        this.testAudioRef.nativeElement.ontimeupdate = null;
    }

    pausePlayback(audioEl: HTMLAudioElement) {
        audioEl.pause();
        this.isPlaying.set(false);
        this.paused.set(true);
        audioEl.ontimeupdate = null;
    }

    setupPlaybackAnalyser() {
        const audio = this.testAudioRef?.nativeElement;
        if (!audio) return;

        this.playbackContext = new AudioContext();
        this.playbackAnalyser = this.playbackContext.createAnalyser();
        this.playbackAnalyser.fftSize = 128;
        this.playbackDataArray = new Uint8Array(
            this.playbackAnalyser.frequencyBinCount,
        );

        const sourceNode = this.playbackContext.createMediaElementSource(audio);
        sourceNode.connect(this.playbackAnalyser);
        this.playbackAnalyser.connect(this.playbackContext.destination);
    }

    async ngOnInit() {
        this.autoStopSub = this.audioDeviceService.autoStopped.subscribe(() => {
            this.toggleMicTest();
        });

        await this.audioDeviceService.loadAvailableDevices();
        this.microphones.set(this.audioDeviceService.microphones());
        this.speakers.set(this.audioDeviceService.speakers());
        this.micValue.set(this.audioDeviceService.micId() ?? '');
        this.speakerValue.set(this.audioDeviceService.speakerId() ?? '');
        this.setupPlaybackAnalyser();
    }

    onMicrophoneChange(micId: string) {
        this.micValue.set(micId);
    }

    onSpeakersChange(speakerId: string) {
        this.speakerValue.set(speakerId);
    }

    save() {
        this.audioDeviceService.saveMicId(this.micValue());
        this.audioDeviceService.saveSpeakerId(this.speakerValue());
        this.saved.emit();
    }

    mapDevicesToDropdown(devices: MediaDeviceInfo[]): IDropdown<string>[] {
        return devices.map((device, index) => ({
            label: device.label || `Device ${index + 1}`,
            value: device.deviceId,
            disabled: false,
        }));
    }

    ngOnDestroy() {
        if (this.updateProgressInterval) {
            clearInterval(this.updateProgressInterval);
        }
        this.autoStopSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
